import React, { useEffect, useMemo, useState } from "react";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import {
  createNotification,
  getNotifications,
  updateNotification,
  deleteNotification,
} from "../../helpers/fakebackend_helper";
import { useCachedRequest, invalidateCache } from "../../helpers/cachedFetch";
import { usePermission } from "../../helpers/usePermission";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/dark.css";
import {
  NOTIFICATION_TYPES,
  DESTINATIONS,
  TOKEN_CHAINS,
  emptyDestination,
  destinationError,
  destinationPayload,
  destinationFromRow,
  destinationLabel,
  typeLabel,
} from "./notificationOptions";

const inputCls =
  "w-full px-3 py-2 text-sm border border-gray-700 rounded-md bg-gray-800 text-gray-100 focus:outline-none focus:ring-1 focus:ring-bl-accent";

// "Opens": where a tap on the notification goes in the app. Only the app's
// current screens are offered (ADMIN_PANEL_MAP.md F3).
const DestinationField = ({ value, onChange, idPrefix }) => {
  const d = value || emptyDestination();
  const set = (k, v) => onChange({ ...d, [k]: v });
  return (
    <div>
      <label className="block text-sm font-normal text-gray-300 mb-1" htmlFor={`${idPrefix}-dest`}>Opens</label>
      <select id={`${idPrefix}-dest`} value={d.key} onChange={(e) => set("key", e.target.value)} className={inputCls}>
        {DESTINATIONS.map((x) => (
          <option key={x.key} value={x.key}>{x.label}</option>
        ))}
      </select>
      {d.key === "token" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
          <select aria-label="Token chain" value={d.network_id} onChange={(e) => set("network_id", e.target.value)} className={inputCls}>
            <option value="">Chain…</option>
            {TOKEN_CHAINS.map((c) => (
              <option key={c.network_id} value={String(c.network_id)}>{c.name}</option>
            ))}
          </select>
          <input aria-label="Token contract" type="text" placeholder="Contract address or native" value={d.contract} onChange={(e) => set("contract", e.target.value)} className={inputCls} />
        </div>
      )}
      {d.key === "url" && (
        <input aria-label="Web page" type="url" placeholder="https://…" value={d.url} onChange={(e) => set("url", e.target.value)} className={`${inputCls} mt-2`} />
      )}
      <p className="mt-1 text-xs text-gray-400">
        App builds that follow notification links open this screen; older builds open Notifications.
      </p>
    </div>
  );
};

const Notifications = ({ embedded = false } = {}) => {
  document.title = "Notifications | BiorGov - BiorGov Admin & Dashboard";
  const canSend = usePermission()("notifications.send");

  const [form, setForm] = useState({
    title: "",
    message: "",
    push: true,
    inapp: true,
    audience: "All Users",
    type: "GENERAL",
    destination: emptyDestination(),
    mode: "now",
    scheduleAt: "",
  });
  const [error, setError] = useState("");

  // Row-level actions (View / Edit / Delete on each notification row).
  const [viewing, setViewing] = useState(null);   // notification row being viewed (read-only)
  const [editing, setEditing] = useState(null);   // SCHEDULED notification row being edited
  const [deletingId, setDeletingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [editError, setEditError] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setError("");
    }, 3000);
  }, [error]);

  const onChange = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const validateForm = () => {
    const errors = [];

    // Required field validation
    if (!form.title.trim()) {
      errors.push("Title is required");
    }

    if (!form.message.trim()) {
      errors.push("Message is required");
    }

    // At least one delivery method must be selected
    if (!form.push && !form.inapp) {
      errors.push("At least one delivery method must be selected");
    }

    const destErr = destinationError(form.destination);
    if (destErr) errors.push(destErr);

    // Schedule validation
    if (form.mode === "later") {
      if (!form.scheduleAt) {
        errors.push(
          "Schedule date and time is required when scheduling for later"
        );
      } else {
        const scheduleDate = new Date(form.scheduleAt);
        const now = new Date();

        if (scheduleDate <= now) {
          errors.push("Schedule date must be in the future");
        }
      }
    }

    return errors;
  };

  const submit = async (e) => {
    e.preventDefault();

    // Validate form before submission
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(", "));
      return;
    }

    try {
      setError("");
      const payload = {
        title: form.title.trim(),
        message: form.message.trim(),
        audience: form.audience,
        type: form.type,
        destination: destinationPayload(form.destination),
        push: form.push,
        inapp: form.inapp,
        mode: form.mode,
        scheduleAt: form.scheduleAt,
      };
      await createNotification(payload);
      await fetchNotifications();
      setForm({ ...form, title: "", message: "", destination: emptyDestination() });
    } catch (err) {
      setError(
        typeof err === "string"
          ? err
          : err?.message || "Failed to create notification"
      );
    }
  };

  const notificationsReq = useCachedRequest({
    key: "notifications:list:100",
    fetcher: async () => {
      // Interceptor unwraps the HTTP body; fields are spread at top level:
      // { status, message, notifications: [...] }.
      const res = await getNotifications({ limit: 100, offset: 0 });
      return { rows: res?.notifications || [], optOut: Boolean(res?.type_opt_out) };
    },
  });
  const list = useMemo(() => notificationsReq.data?.rows || [], [notificationsReq.data]);
  const optOutOn = Boolean(notificationsReq.data?.optOut);
  const scheduled = useMemo(() => list.filter((n) => n.status === "SCHEDULED"), [list]);
  const sent = useMemo(() => list.filter((n) => n.status === "SENT" || n.status === "SENDING" || n.status === "FAILED"), [list]);
  // eslint-disable-next-line no-unused-vars
  const loading = notificationsReq.loading;
  useEffect(() => {
    if (notificationsReq.error && !notificationsReq.data) {
      setError(typeof notificationsReq.error === "string" ? notificationsReq.error : notificationsReq.error?.message || "Failed to load notifications");
    }
  }, [notificationsReq.error, notificationsReq.data]);

  const fetchNotifications = async () => {
    invalidateCache("notifications:");
    await notificationsReq.refetch();
  };

  const openView = (n) => setViewing(n);
  const closeView = () => setViewing(null);

  const openEdit = (n) => {
    setEditError("");
    setEditing(n);
    setEditDraft({
      title: n.title || "",
      message: n.message || "",
      audience: n.audience || "All Users",
      type: n.type || "GENERAL",
      destination: destinationFromRow(n),
      push: Boolean(n.delivery_push),
      inapp: Boolean(n.delivery_inapp),
      scheduleAt: n.schedule_at ? new Date(n.schedule_at).toISOString().slice(0, 16) : "",
    });
  };
  const closeEdit = () => {
    setEditing(null);
    setEditDraft(null);
    setEditError("");
  };

  const onEditField = (key, value) =>
    setEditDraft((prev) => (prev ? { ...prev, [key]: value } : prev));

  const saveEdit = async (e) => {
    e?.preventDefault?.();
    if (!editing || !editDraft) return;
    if (!editDraft.title.trim() || !editDraft.message.trim()) {
      setEditError("Title and message are required");
      return;
    }
    if (!editDraft.push && !editDraft.inapp) {
      setEditError("At least one delivery method must be selected");
      return;
    }
    const editDestErr = destinationError(editDraft.destination);
    if (editDestErr) {
      setEditError(editDestErr);
      return;
    }
    if (!editDraft.scheduleAt) {
      setEditError("Schedule time is required");
      return;
    }
    if (new Date(editDraft.scheduleAt) <= new Date()) {
      setEditError("Schedule time must be in the future");
      return;
    }
    try {
      setEditSaving(true);
      setEditError("");
      await updateNotification(editing.id, {
        title: editDraft.title.trim(),
        message: editDraft.message.trim(),
        audience: editDraft.audience,
        type: editDraft.type,
        destination: destinationPayload(editDraft.destination),
        push: editDraft.push,
        inapp: editDraft.inapp,
        scheduleAt: editDraft.scheduleAt,
      });
      closeEdit();
      await fetchNotifications();
    } catch (err) {
      setEditError(
        typeof err === "string"
          ? err
          : err?.message || "Failed to update notification",
      );
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (n) => {
    if (!n) return;
    if (!window.confirm(`Delete notification "${n.title}"? This cannot be undone.`)) return;
    try {
      setDeletingId(n.id);
      setError("");
      await deleteNotification(n.id);
      await fetchNotifications();
    } catch (err) {
      setError(
        typeof err === "string"
          ? err
          : err?.message || "Failed to delete notification",
      );
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "-";
    try {
      return new Date(iso).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    } catch (_) {
      return iso;
    }
  };

  return (
    <div className={embedded ? "" : "min-h-screen"}>
      <div className={embedded ? "w-full min-w-0" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"}>
        {!embedded && <BreadCrumb title="Notifications" pageTitle="In the app" />}

        {/* Compose Notification Card — hidden for view-only roles. */}
        {canSend && (
        <div className="bl-glass mb-6">
          <div className="px-6 py-4 border-b border-gray-700">
            <h5 className="text-lg font-normal text-gray-100 mb-0">
              Compose Notification
            </h5>
          </div>
          <div className="p-6">
            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
                {error}
              </div>
            )}
            <form onSubmit={submit}>
              <div className="mb-4">
                <label className="block text-sm font-normal text-gray-300 mb-1">
                  Notification Title
                </label>
                <input
                  type="text"
                  placeholder="Enter notification title..."
                  value={form.title}
                  onChange={(e) => onChange("title", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-700 rounded-md shadow-sm placeholder-gray-400 bg-gray-800 text-gray-100 focus:outline-none focus:ring-1 focus:ring-bl-accent focus:border-bl-accent transition-colors"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-normal text-gray-300 mb-1">
                  Message
                </label>
                <textarea
                  rows="5"
                  placeholder="Enter your notification message..."
                  value={form.message}
                  onChange={(e) => onChange("message", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-700 rounded-md shadow-sm placeholder-gray-400 bg-gray-800 text-gray-100 focus:outline-none focus:ring-1 focus:ring-bl-accent focus:border-bl-accent transition-colors resize-y"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-normal text-gray-300 mb-2">
                    Delivery Method
                  </label>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <input
                        id="push"
                        type="checkbox"
                        checked={form.push}
                        onChange={(e) => onChange("push", e.target.checked)}
                        className="h-4 w-4 text-gray-400 focus:ring-bl-accent border-gray-700 rounded"
                      />
                      <label htmlFor="push" className="ml-2 text-sm text-gray-300">
                        Push notification (outside app)
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="inapp"
                        type="checkbox"
                        checked={form.inapp}
                        onChange={(e) => onChange("inapp", e.target.checked)}
                        className="h-4 w-4 text-gray-400 focus:ring-bl-accent border-gray-700 rounded"
                      />
                      <label htmlFor="inapp" className="ml-2 text-sm text-gray-300">
                        In-app notification
                      </label>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-normal text-gray-300 mb-1">
                    Target Audience
                  </label>
                  <select
                    value={form.audience}
                    onChange={(e) => onChange("audience", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-700 rounded-md shadow-sm bg-gray-800 text-gray-100 focus:outline-none focus:ring-1 focus:ring-bl-accent focus:border-bl-accent transition-colors"
                  >
                    <option value="All Users">All Users</option>
                    <option value="iOS users">iOS users</option>
                    <option value="Android users">Android users</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-normal text-gray-300 mb-1">
                    Notification Type
                  </label>
                  <select
                    value={form.type}
                    onChange={(e) => onChange("type", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-700 rounded-md shadow-sm bg-gray-800 text-gray-100 focus:outline-none focus:ring-1 focus:ring-bl-accent focus:border-bl-accent transition-colors"
                  >
                    {NOTIFICATION_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-400">
                    {(NOTIFICATION_TYPES.find((t) => t.value === form.type) || {}).hint}
                    {form.type !== "GENERAL" && !optOutOn && " Not yet applied: everyone gets it until the type switches are turned on server-side."}
                  </p>
                </div>
              </div>

              <DestinationField idPrefix="compose" value={form.destination} onChange={(v) => onChange("destination", v)} />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                <div>
                  <label className="block text-sm font-normal text-gray-300 mb-2">
                    Schedule
                  </label>
                  <div className="flex flex-wrap gap-4">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="schedule"
                        id="sendnow"
                        checked={form.mode === "now"}
                        onChange={() => {
                          onChange("scheduleAt", "");
                          onChange("mode", "now");
                        }}
                        className="h-4 w-4 text-gray-400 focus:ring-bl-accent border-gray-700"
                      />
                      <label htmlFor="sendnow" className="ml-2 text-sm text-gray-300">
                        Send now
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="schedule"
                        id="sendlater"
                        checked={form.mode === "later"}
                        onChange={() => onChange("mode", "later")}
                        className="h-4 w-4 text-gray-400 focus:ring-bl-accent border-gray-700"
                      />
                      <label htmlFor="sendlater" className="ml-2 text-sm text-gray-300">
                        Schedule for later
                      </label>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-normal text-gray-300 mb-1" htmlFor="schedule_time">
                    Date & Time
                  </label>
                  <Flatpickr
                    data-enable-time
                    id="schedule_time"
                    className="w-full px-3 py-2 border border-gray-700 rounded-md shadow-sm bg-gray-800 text-gray-100 focus:outline-none focus:ring-1 focus:ring-bl-accent focus:border-bl-accent transition-colors"
                    placeholder="Select Date & Time"
                    options={{
                      enableTime: true,
                      dateFormat: "Y-m-d H:i",
                      minDate: "today",
                      disableMobile: true,
                    }}
                    disabled={form.mode !== "later"}
                    value={form.scheduleAt}
                    onChange={([date]) => {
                      onChange(
                        "scheduleAt",
                        date ? date.toISOString().slice(0, 16) : ""
                      );
                    }}
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    type="submit"
                    className="px-6 py-2 text-sm font-normal text-white bg-bl-accent rounded-md hover:bg-bl-accent-light focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                  >
                    Send Notification
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
        )}

        {/* Scheduled Notifications */}
        <div className="bl-glass mb-6">
          <div className="px-6 py-4 border-b border-gray-700">
            <h5 className="text-lg font-normal text-gray-100 mb-0">
              Scheduled Notifications
            </h5>
          </div>
          <div className="p-6 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-bl-accent/5">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Scheduled Time
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Sent At
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Target Audience
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Delivery Methods
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-gray-800 divide-y divide-gray-700">
                {scheduled.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-sm text-gray-400">
                      No scheduled notifications
                    </td>
                  </tr>
                ) : (
                  scheduled.map((n) => (
                    <tr key={n.id} className="hover:bg-bl-accent/10/50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-100">
                        {n.title}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                        {formatDate(n.schedule_at)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                        {formatDate(n.sent_at)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                        {n.audience}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex gap-1">
                          {n.delivery_push && (
                            <span className="px-2 py-1 text-xs font-normal rounded bg-bl-accent/16 text-bl-accent-light">
                              Push
                            </span>
                          )}
                          {n.delivery_inapp && (
                            <span className="px-2 py-1 text-xs font-normal rounded bg-cyan-500/16 text-cyan-300">
                              In-App
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-normal rounded bg-amber-500/16 text-amber-300">
                          Scheduled
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => openView(n)}
                            className="px-3 py-1 text-xs font-normal text-gray-300 bg-gray-800 border border-gray-700 rounded-md hover:bg-bl-accent/10 transition-colors"
                          >
                            View
                          </button>
                          <button
                            type="button"
                            onClick={() => openEdit(n)}
                            className="px-3 py-1 text-xs font-normal text-gray-300 bg-gray-800 border border-gray-700 rounded-md hover:bg-bl-accent/10 transition-colors"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(n)}
                            disabled={deletingId === n.id}
                            className="px-3 py-1 text-xs font-normal text-red-400 bg-gray-800 border border-red-500/40 rounded-md hover:bg-red-500/10 transition-colors disabled:opacity-50"
                          >
                            {deletingId === n.id ? "Deleting…" : "Delete"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Sent Notifications */}
        <div className="bl-glass">
          <div className="px-6 py-4 border-b border-gray-700">
            <h5 className="text-lg font-normal text-gray-100 mb-0">
              Delivery History
            </h5>
          </div>
          <div className="p-6 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-bl-accent/5">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Sent At
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Target Audience
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Delivery Methods
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-gray-800 divide-y divide-gray-700">
                {sent.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-sm text-gray-400">
                      No sent notifications
                    </td>
                  </tr>
                ) : (
                  sent.map((n) => (
                    <tr key={n.id} className="hover:bg-bl-accent/10/50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-100">
                        {n.title}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                        {formatDate(n.sent_at)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                        {n.audience}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex gap-1">
                          {n.delivery_push && (
                            <span className="px-2 py-1 text-xs font-normal rounded bg-bl-accent/16 text-bl-accent-light">
                              Push
                            </span>
                          )}
                          {n.delivery_inapp && (
                            <span className="px-2 py-1 text-xs font-normal rounded bg-cyan-500/16 text-cyan-300">
                              In-App
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {n.status === "SENT" ? (
                          <span className="px-2 py-1 text-xs font-normal rounded bg-green-500/16 text-green-300">
                            Published
                          </span>
                        ) : n.status === "SENDING" ? (
                          <span className="px-2 py-1 text-xs font-normal rounded bg-cyan-500/16 text-cyan-300">
                            Sending
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-normal rounded bg-red-500/16 text-red-300">
                            Failed
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => openView(n)}
                            className="px-3 py-1 text-xs font-normal text-gray-300 bg-gray-800 border border-gray-700 rounded-md hover:bg-bl-accent/10 transition-colors"
                          >
                            View
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(n)}
                            disabled={deletingId === n.id}
                            className="px-3 py-1 text-xs font-normal text-red-400 bg-gray-800 border border-red-500/40 rounded-md hover:bg-red-500/10 transition-colors disabled:opacity-50"
                          >
                            {deletingId === n.id ? "Deleting…" : "Delete"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* View modal (read-only details) */}
      {viewing && (
        <div
          onClick={closeView}
          className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/60"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bl-glass-strong max-w-lg w-full p-6 rounded-xl"
            style={{ background: "rgba(14,13,41,0.96)" }}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-100 m-0">Notification</h3>
              <button onClick={closeView} aria-label="Close" className="text-gray-400 hover:text-gray-100 text-xl leading-none">✕</button>
            </div>
            <dl className="text-sm space-y-2">
              <Field label="Title" value={viewing.title} />
              <Field label="Message" value={viewing.message} multiline />
              <Field label="Status" value={viewing.status} />
              <Field label="Audience" value={viewing.audience} />
              <Field label="Type" value={typeLabel(viewing.type)} />
              <Field label="Opens" value={destinationLabel(viewing.destination)} />
              <Field
                label="Delivery"
                value={[
                  viewing.delivery_push ? "Push" : null,
                  viewing.delivery_inapp ? "In-app" : null,
                ].filter(Boolean).join(" · ") || "—"}
              />
              <Field label="Scheduled for" value={formatDate(viewing.schedule_at)} />
              <Field label="Sent at" value={formatDate(viewing.sent_at)} />
              <Field label="Created" value={formatDate(viewing.created_at)} />
            </dl>
            <div className="flex justify-end mt-5">
              <button onClick={closeView} className="px-4 py-2 text-sm text-gray-300 bg-gray-800 border border-gray-700 rounded-md hover:bg-bl-accent/10">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal (scheduled notifications only) */}
      {editing && editDraft && (
        <div
          onClick={closeEdit}
          className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/60"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bl-glass-strong max-w-lg w-full p-6 rounded-xl"
            style={{ background: "rgba(14,13,41,0.96)" }}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-100 m-0">Edit scheduled notification</h3>
              <button onClick={closeEdit} aria-label="Close" className="text-gray-400 hover:text-gray-100 text-xl leading-none">✕</button>
            </div>
            {editError && (
              <div className="mb-3 p-3 bg-red-500/10 border border-red-500/30 rounded-md text-sm text-red-400">{editError}</div>
            )}
            <form onSubmit={saveEdit} className="space-y-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Title</label>
                <input
                  type="text"
                  value={editDraft.title}
                  onChange={(e) => onEditField("title", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-700 rounded-md bg-gray-800 text-gray-100 focus:outline-none focus:ring-1 focus:ring-bl-accent"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Message</label>
                <textarea
                  rows="4"
                  value={editDraft.message}
                  onChange={(e) => onEditField("message", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-700 rounded-md bg-gray-800 text-gray-100 focus:outline-none focus:ring-1 focus:ring-bl-accent resize-y"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Audience</label>
                  <select
                    value={editDraft.audience}
                    onChange={(e) => onEditField("audience", e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-700 rounded-md bg-gray-800 text-gray-100"
                  >
                    <option>All Users</option>
                    <option>iOS users</option>
                    <option>Android users</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Type</label>
                  <select
                    value={editDraft.type}
                    onChange={(e) => onEditField("type", e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-700 rounded-md bg-gray-800 text-gray-100"
                  >
                    {NOTIFICATION_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <DestinationField idPrefix="edit" value={editDraft.destination} onChange={(v) => onEditField("destination", v)} />
              <div>
                <label className="block text-xs text-gray-400 mb-1">Delivery</label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center text-sm text-gray-300">
                    <input
                      type="checkbox"
                      checked={editDraft.push}
                      onChange={(e) => onEditField("push", e.target.checked)}
                      className="h-4 w-4 mr-2"
                    />
                    Push
                  </label>
                  <label className="flex items-center text-sm text-gray-300">
                    <input
                      type="checkbox"
                      checked={editDraft.inapp}
                      onChange={(e) => onEditField("inapp", e.target.checked)}
                      className="h-4 w-4 mr-2"
                    />
                    In-app
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Schedule for</label>
                <input
                  type="datetime-local"
                  value={editDraft.scheduleAt}
                  onChange={(e) => onEditField("scheduleAt", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-700 rounded-md bg-gray-800 text-gray-100"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={closeEdit} className="px-4 py-2 text-sm text-gray-300 bg-gray-800 border border-gray-700 rounded-md hover:bg-bl-accent/10">Cancel</button>
                <button type="submit" disabled={editSaving} className="px-4 py-2 text-sm text-white bg-bl-accent rounded-md hover:bg-bl-accent-light disabled:opacity-50">
                  {editSaving ? "Saving…" : "Save changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, multiline }) => (
  <div className="grid grid-cols-[120px_1fr] gap-3 items-baseline">
    <dt className="text-xs uppercase tracking-wider text-gray-400">{label}</dt>
    <dd className={`text-sm text-gray-100 ${multiline ? "whitespace-pre-wrap break-words" : "break-words"}`}>
      {value == null || value === "" ? <span className="text-gray-500">—</span> : value}
    </dd>
  </div>
);

export default Notifications;
